import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
    ApplicationFacade,
    Itinerary,
    JourneyPlannerItineraryTransportMode,
    OtpUtil,
    TransportMode
} from '@ridango/components';
import Env from '@ridango/core/env';
import { Temporal } from '@js-temporal/polyfill';
import {
    AppStartupService,
    DisruptionMessageType,
    DisruptionMessageSeverity,
    matchesStopId,
    TimeUtil
} from '@ridango/core';
import { JourneyItineraryUtils } from './journey-itinerary.utils';

@Component({
    selector: 'rwidget-journey-itinerary',
    templateUrl: './journey-itinerary.component.html',
    styleUrls: ['./journey-itinerary.component.scss']
})
export class JourneyItineraryComponent implements OnInit, OnChanges {
    _currentTime: number;
    @Input() set currentTime(value: number) {
        this._currentTime = TimeUtil.removeMilliseconds(value);
    }
    @Input() itinerary: Itinerary;
    @Input() summary: 'default' | 'short' = 'default';
    @Input() itineraryDetailsPage = false;

    DisruptionMessageSeverity = DisruptionMessageSeverity;
    highContrast$ = this.applicationFacade.highContrast$;
    assetsPath$ = this.applicationFacade.assetsPath$;
    transportModeEnum = JourneyPlannerItineraryTransportMode;
    otpUtil = OtpUtil;
    isRealtime: boolean;
    isDelayed: boolean;
    isDelayedThreshold: boolean;
    timeBetweenIncludes: boolean;
    showLeavesIn: boolean;
    hasDeparted: boolean;
    getBoardAt: number;
    isDisrupted: false | undefined | boolean;
    isDisruptedWithSeverity: false | undefined | boolean | DisruptionMessageSeverity;
    isCancelledTrip: boolean;
    realdataStartTime: number;
    realdataEndTime: number;
    hasFootnote: false | undefined | boolean;
    isFlexibleTrip: boolean;
    isOverOneHour: boolean;
    isDepartureApproximateTime = false;
    isArrivalApproximateTime = false;
    isDepartureThresholdExceeded = false;
    isArrivalThresholdExceeded = false;
    isOptimalJourneyIconVisible = false;
    thresholdMinutes = this.env.FIXED_TIME_THRESHOLD_MINUTES ?? 0;
    journeyAriaLabel = '';

    constructor(
        @Inject('ENV') public env: Env,
        private applicationFacade: ApplicationFacade,
        private appStartup: AppStartupService,
        private translate: TranslateService
    ) {}

    timeZone = this.appStartup.getTimezone();
    timeZoneOffset: string;

    ngOnInit(): void {
        this.timeZoneOffset = this.getOffsetForTrip(this.itinerary.startTime);
        this.isRealtime =
            this.env.JOURNEY_PLANNER?.REALTIME_AVAILABLE &&
            (this.itinerary?.legs.some((leg) => leg?.realTime) || this.itinerary?.realTime);

        this.isDelayed = !!this.itinerary?.journeyEndArrivalDelay || !!this.itinerary?.journeyStartDepartureDelay;
        const scheduleTimeThresholdEarly = -this.env.JOURNEY_PLANNER?.SCHEDULE_TIME_THRESHOLD_EARLY;
        const scheduleTimeThresholdLate = this.env.JOURNEY_PLANNER?.SCHEDULE_TIME_THRESHOLD_LATE;
        this.isDelayedThreshold =
            (scheduleTimeThresholdEarly || scheduleTimeThresholdLate) &&
            (Math.floor(this.itinerary?.journeyEndArrivalDelay / 60) > scheduleTimeThresholdLate ||
                Math.ceil(this.itinerary?.journeyEndArrivalDelay) / 60 < scheduleTimeThresholdEarly ||
                Math.floor(this.itinerary?.journeyStartDepartureDelay) / 60 > scheduleTimeThresholdLate ||
                Math.ceil(this.itinerary?.journeyStartDepartureDelay) / 60 < scheduleTimeThresholdEarly);
        this.update();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.currentTime || changes.itinerary) {
            this.update();
        }
    }

    update() {
        this.setTimeBetweenIncludes();
        this.setShowLeavesIn();
        this.setGetBoardAt();
        this.setHasDeparted();
        this.setsDisrupted();
        this.setIsCancelledTrip();
        this.setRealdataStartTime();
        this.setRealdataEndTime();
        this.setHasFootnote();
        this.setIsFlexibleTrip();
        this.setIsOverOneHour();
        this.setIsOverOneHour();
        this.prepareDisplayTimes();
        this.setOptimalJourneyIcon();
        this.buildJourneyAriaLabel();
    }
    private getOffsetForTrip(startTime: number): string {
        const instant = Temporal.Instant.fromEpochMilliseconds(startTime);
        return instant.toZonedDateTimeISO(this.timeZone).offset.toString();
    }

    private prepareDisplayTimes(): void {
        const firstTransitLeg = this.itinerary.legs.find((leg) => leg.mode !== TransportMode.Walk);
        if (firstTransitLeg && this.env.TIMEPOINTS_ENABLED) {
            this.isDepartureApproximateTime = !firstTransitLeg.depIsTimingPoint;
        } else {
            this.isDepartureApproximateTime = false;
        }

        const lastTransitLeg = [...this.itinerary.legs].reverse().find((leg) => leg.mode !== TransportMode.Walk);
        if (lastTransitLeg && this.env.TIMEPOINTS_ENABLED) {
            this.isArrivalApproximateTime = !lastTransitLeg.arrIsTimingPoint;
        } else {
            this.isArrivalApproximateTime = false;
        }

        this.isDepartureThresholdExceeded = false;
        this.isArrivalThresholdExceeded = false;

        if (!this.isRealtime) {
            return;
        }

        if (firstTransitLeg) {
            const isFixed = firstTransitLeg.depIsTimingPoint;
            const delayInSeconds = firstTransitLeg.departureDelay;
            let thresholdMinutes: number;

            if (this.env.TIMEPOINTS_ENABLED) {
                thresholdMinutes = isFixed ? this.thresholdMinutes : this.env.APPROXIMATE_TIME_THRESHOLD_MINUTES;
            } else {
                thresholdMinutes = this.thresholdMinutes;
            }

            if (delayInSeconds != null) {
                const delayMinutes = Math.floor(Math.abs(delayInSeconds / 60));
                if (delayMinutes >= thresholdMinutes) {
                    const realDepartureTime = this.itinerary.startTime + delayInSeconds * 1000;
                    const areMinutesDifferent =
                        Math.floor(new Date(this.itinerary.startTime).getTime() / 60000) !==
                        Math.floor(new Date(realDepartureTime).getTime() / 60000);
                    if (areMinutesDifferent) {
                        this.isDepartureThresholdExceeded = true;
                    }
                }
            }
        }

        if (lastTransitLeg) {
            const isFixed = lastTransitLeg.arrIsTimingPoint;
            const delayInSeconds = lastTransitLeg.arrivalDelay;
            let thresholdMinutes: number;

            if (this.env.TIMEPOINTS_ENABLED) {
                thresholdMinutes = isFixed ? this.thresholdMinutes : this.env.APPROXIMATE_TIME_THRESHOLD_MINUTES;
            } else {
                thresholdMinutes = this.thresholdMinutes;
            }

            if (delayInSeconds != null) {
                const delayMinutes = Math.floor(Math.abs(delayInSeconds / 60));
                if (delayMinutes >= thresholdMinutes) {
                    const realArrivalTime = this.itinerary.endTime + delayInSeconds * 1000;
                    const areMinutesDifferent =
                        Math.floor(new Date(this.itinerary.endTime).getTime() / 60000) !==
                        Math.floor(new Date(realArrivalTime).getTime() / 60000);
                    if (areMinutesDifferent) {
                        this.isArrivalThresholdExceeded = true;
                    }
                }
            }
        }
    }

    setRealdataStartTime() {
        this.realdataStartTime = this.itinerary?.startTime + (this.itinerary?.journeyStartDepartureDelay || 0) * 1000;
    }

    setIsCancelledTrip() {
        this.isCancelledTrip = this.itinerary?.legs.some((item) => item.canceled) ?? false;
    }

    setRealdataEndTime() {
        this.realdataEndTime = this.itinerary?.endTime + (this.itinerary?.journeyEndArrivalDelay || 0) * 1000;
    }

    setsDisrupted() {
        this.isDisrupted =
            this.env.JOURNEY_PLANNER?.SHOW_DISRUPTION_MESSAGES &&
            this.itinerary?.legs.some((leg) => {
                return (
                    leg.disruptionMessages?.length > 0 &&
                    leg.disruptionMessages?.some((disruptionMessage) => {
                        if (!disruptionMessage.informedEntities) {
                            return false;
                        }

                        const hasMatchingStop = disruptionMessage.informedEntities.some((entity) => {
                            const allStops = [leg.from, leg.to, ...(leg.intermediateStops || [])];
                            return allStops.some((stop) => {
                                return (
                                    matchesStopId(entity.stopId, stop.stopId, stop.stopCode) ||
                                    (entity.stopId === null && entity.routeId?.toString() === leg.routeId)
                                );
                            });
                        });

                        if (!hasMatchingStop) {
                            return false;
                        }

                        return disruptionMessage.informedEntities.some((affectedEntity) => {
                            if (affectedEntity?.tripId) {
                                return affectedEntity.tripId === leg?.tripId;
                            }

                            const busDisruptionType =
                                disruptionMessage.messageType === DisruptionMessageType.ALERT_BUS ||
                                disruptionMessage.messageType === DisruptionMessageType.ALERT_BUS_AND_STOP;
                            const stopDisruptionType =
                                disruptionMessage.messageType === DisruptionMessageType.ALERT_STOP ||
                                disruptionMessage.messageType === DisruptionMessageType.ALERT_BUS_AND_STOP;
                            const genericDisruptionType =
                                disruptionMessage.messageType === DisruptionMessageType.ALERT_GENERIC;

                            if (this.env.DIRECTIONAL_ALERTS) {
                                if (busDisruptionType || stopDisruptionType) {
                                    this.isDisruptedWithSeverity = disruptionMessage.severity;

                                    if (stopDisruptionType && !affectedEntity.routeId) {
                                        return true;
                                    }

                                    return (
                                        String(affectedEntity.routeId) === String(leg.routeId) &&
                                        (String(affectedEntity.tripDirection) === String(leg.tripDirection) ||
                                            affectedEntity.tripDirection == null)
                                    );
                                }
                            }

                            return genericDisruptionType && String(affectedEntity.routeId) === String(leg.routeId);
                        });
                    })
                );
            });
    }

    setHasFootnote() {
        this.hasFootnote =
            this.env.JOURNEY_PLANNER?.SHOW_DISRUPTION_MESSAGES &&
            this.itinerary?.legs.some((leg: any) => leg.alerts?.some((alert) => alert.effect === 'INFORMATION'));
    }

    setGetBoardAt() {
        const leg = this.itinerary.legs.find((l) => l.mode !== TransportMode.Walk);
        const firstWalkLeg = this.itinerary.legs.find(
            (l) => l.mode === TransportMode.Walk && this.itinerary.startTime === l.startTime
        );
        const delay = (this.itinerary?.journeyStartDepartureDelay ?? leg?.departureDelay) || 0;

        this.getBoardAt = (firstWalkLeg?.endTime ?? this.itinerary.startTime) + delay * 1000;
    }

    setShowLeavesIn() {
        const timezone = this.appStartup.getTimezone();
        const startDate = this.otpUtil.unixToDate(this._currentTime, timezone);
        let endDate = this.otpUtil.unixToDate(this._currentTime, timezone);
        if (this.itinerary?.startTime) {
            endDate = this.otpUtil.unixToDate(this.itinerary?.startTime, timezone);
        }
        const timeDifferenceInMinutes = startDate.until(endDate).total({ unit: 'minute' });

        this.showLeavesIn = timeDifferenceInMinutes > 0 && timeDifferenceInMinutes < 60;
    }

    setHasDeparted(): boolean {
        if (!this.env.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS) {
            return false;
        }
        const boardAt = this.getBoardAt;
        if (this.env.JOURNEY_PLANNER.HIDE_LEAVE_NOW) {
            const departureDelayMilliseconds =
                (this.env.JOURNEY_PLANNER.SHOW_DEPARTED_DELAY_SECONDS || 0) * TimeUtil.MILLISECONDS_IN_SECOND;
            this.hasDeparted = this._currentTime - boardAt >= departureDelayMilliseconds;
        } else {
            this.hasDeparted = this._currentTime > boardAt;
        }
        return this.hasDeparted;
    }

    setTimeBetweenIncludes() {
        const timezone = this.appStartup.getTimezone();
        const startDate = this.otpUtil.unixToDate(this._currentTime, timezone);
        let endDate = null;
        if (this.itinerary?.startTime) {
            endDate = this.otpUtil.unixToDate(this.itinerary?.startTime, timezone);
        }
        if (endDate && startDate.until(endDate).total({ unit: 'minute' }) > 0) {
            const timeBetween = startDate.until(endDate).total({ unit: 'hour' });
            this.timeBetweenIncludes = timeBetween > 0;
        }
        this.timeBetweenIncludes = false;
    }

    getMinutesDuration(duration: Temporal.Duration) {
        return duration.round('minute').total('minute');
    }

    setIsFlexibleTrip() {
        this.isFlexibleTrip = !!this.itinerary?.legs?.find(
            (l) => l.mode === JourneyPlannerItineraryTransportMode.Flexible
        );
    }

    setIsOverOneHour() {
        const timezone = this.appStartup.getTimezone();
        const startDate = this.otpUtil.unixToDate(this._currentTime, timezone);
        const endDate = this.otpUtil.unixToDate(this.itinerary.startTime, timezone);
        const timeDifferenceInMinutes = startDate.until(endDate).total({ unit: 'minute' });

        this.isOverOneHour = timeDifferenceInMinutes > 60;
    }

    setOptimalJourneyIcon() {
        const isIconVisible = this.env.JOURNEY_PLANNER?.SHOW_LAST_DEPARTED_JOURNEYS?.OPTIMAL_JOURNEY_ICON_VISIBLE;

        this.isOptimalJourneyIconVisible =
            (this.itinerary?.isMostOptimal ?? false) && !this.hasDeparted && isIconVisible !== false;
    }

    private buildJourneyAriaLabel(): void {
        this.journeyAriaLabel = JourneyItineraryUtils.buildJourneyAriaLabel({
            itinerary: this.itinerary,
            realTimeStartTime: this.realdataStartTime,
            realTimeEndTime: this.realdataEndTime,
            boardAt: this.getBoardAt,
            hasDeparted: this.hasDeparted,
            isRealtime: this.isRealtime,
            locale: this.appStartup.getLocale(),
            timezone: this.timeZone,
            translate: this.translate.instant.bind(this.translate)
        });
    }
}
